import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Typography,
  Alert, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { AxiosError } from 'axios';
import { useAuth } from '../shared/contexts/AuthContext';
import { DEFAULT_CATALOGUE_SETTINGS } from '../shared/types';
import { API_BASE_URL } from '../shared/services/apiClient';

export const AcceptInvitePage: React.FC = () => {
  const { acceptInvite } = useAuth();
  const params = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  // Accept both /invite/:token and /invite?token=...
  const token = params.token ?? searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const settings = DEFAULT_CATALOGUE_SETTINGS;
  const logoUrl = `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(settings.logo_file)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('This invite link is invalid or incomplete.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await acceptInvite(token, password);
    } catch (err) {
      const msg =
        err instanceof AxiosError
          ? (err.response?.data as { detail?: string })?.detail ?? err.message
          : 'Could not activate your account.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F7F6F2', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400, borderRadius: 2 }} component="form" onSubmit={handleSubmit}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src={logoUrl}
            alt="Brand logo"
            sx={{ height: 64, objectFit: 'contain' }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </Box>

        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#363D4C', fontWeight: 700 }}>
          Activate your account
        </Typography>
        <Typography variant="body2" align="center" sx={{ color: '#777', mb: 3 }}>
          Choose a password to access the product catalogue.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="Password"
          type={show ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          fullWidth
          autoFocus
          autoComplete="new-password"
          sx={{ mb: 2 }}
          disabled={loading}
          helperText="At least 8 characters"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShow(v => !v)} edge="end" size="small">
                  {show ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm password"
          type={show ? 'text' : 'password'}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          fullWidth
          autoComplete="new-password"
          sx={{ mb: 3 }}
          disabled={loading}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !password || !confirm}
          sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8d4a88' }, fontWeight: 700 }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Activate & sign in'}
        </Button>
      </Paper>
    </Box>
  );
};

export default AcceptInvitePage;
